/**
 * @file useMarkAsGrad.js
 * @module src/js/hooks/useMarkAsGrad
 * @description
 * **A hook to mark a member's profile as graduated.**
 * - Utilizes React contexts to manage user state and modal messages.
 * - Returns a function that:
 *   - Sends a PUT request to the backend to update the member's graduation status.
 *   - Updates the profile data in the frontend upon successful update.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useFetchFromBackend. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserProvider.js';
import { useTheme } from '../contexts/ThemeProvider.js';
import { useModal } from '../contexts/ModalProvider.js';
import useFetchFromBackend from '../hooks/useFetchFromBackend.js';

export default function useMarkAsGrad({
    setUpdating,
    setProfileData,
    setUpdatedProfileData,
}) {
    const { pathname } = useLocation();
    const { theme } = useTheme();
    const { setUgroup } = useUser();
    const { showModalMessage, confirm } = useModal();
    const fetchFromBackend = useFetchFromBackend();

    if (typeof setUpdating !== 'function' ||
        typeof setProfileData !== 'function' ||
        typeof setUpdatedProfileData !== 'function') {
        throw new Error("All setter functions must be provided to useMarkAsGrad hook.");
    }
    
    const markAsGrad = useCallback(async ({
        id,
        institute,
        level,
    }) => {
        if (!id || !institute || !level) {
            throw new Error("ID, institute, and level are required to mark as graduated.");
        }
        
        try {
            setUpdating(true);

            // Ask the user first
            const ok = await confirm({
                message: "Are you sure you want to mark yourself as graduated? This will update your graduation degree and year.\nOnly the administrator can undo this action.",
                confirmLabel: "Mark",
                cancelLabel: "Cancel",
                buttonColor: theme === 'dark' ? 'lightgreen' : 'green',
            });

            if (!ok) {
                showModalMessage({ type: "success", content: "Cancelled." });
                return;
            }

            // User confirmed, now perform update
            showModalMessage({ type: "info", content: "Updating..." });

            const final_grad = `${
                institute === '電子所' ? '電子工程' :
                institute === '電機所' ? '電機工程' :
                institute === '資工所' ? '資訊工程' :
                institute === '重點科技學院' ? '重點科技學院' :
                institute === '電資學院資料科學學程' ? '資料科學學位學程' :
                '_'
            }${
                level === 1 ? '碩士' :
                level === 2 ? '博士' :
                level === 4 ? '博士後研究員' :
                '_'
            }`;
            if (final_grad.includes('_')) {
                showModalMessage({ type: "error", content: "Failed to update. Please try again." });
                throw new Error("Invalid institute or level for graduation.");
            }

            const data = await fetchFromBackend({
                request: `/api/profile/mark-grad/${id}`,
                method: "PUT",
                body: { final_grad },
                requireAuth: true,
                from: pathname,
            });

            if (data && data.success) {
                const data = await fetchFromBackend({ request: `/api/profile/${id}` });
                setProfileData(data || {});
                setUpdatedProfileData(data || {});
                setUgroup('grad');
                showModalMessage({ type: "success", content: "Congratulations! Your profile has been updated." });

            } else if (data && data.error) {
                console.error("Failed to update:", data.error);
                showModalMessage({ type: "error", content: "Failed to update. Please try again." });
            }

        } catch (error) {
            console.error("An error occurred:", error);
            showModalMessage({ type: "error", content: "An error occurred. Please try again." });

        } finally {
            setUpdating(false);
        }
    }, [confirm, fetchFromBackend, pathname, setProfileData, setUpdatedProfileData, setUgroup, showModalMessage, theme, setUpdating]);

    return markAsGrad;
}